import { MutationFilters, useMutation, useQueryClient } from "@tanstack/react-query";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import xor from "lodash/xor";
import { updateBooking, UpdateBookingPayload } from "PFApp/booking/services/api";
import { bookingsBulkUpdate } from "PFCore/services/bookings_bulk_operation";
import { Id } from "PFTypes";
import { MutationOptions } from "PFTypes/request_options";
import { useCallback } from "react";

import { useProfileInvalidate } from "../profile";
import { bookingKeys } from "./query_keys";

const mutationKey = ["booking-update"];

export const useBookingUpdate = (options?: MutationOptions<UpdateBookingPayload>) => {
  const cache = useQueryClient();
  const { invalidateProfile, invalidateProfileWithBooking } = useProfileInvalidate();

  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateBookingPayload) => {
      const bookingCacheKey = bookingKeys.single(payload.id);
      await cache.cancelQueries({ queryKey: bookingCacheKey });
      if (!!payload.bookingGroupId && !payload.id) {
        return bookingsBulkUpdate({
          filters: [{ bookingGroupId: payload.bookingGroupId }],
          updates: payload
        });
      }
      return updateBooking(payload);
    },
    mutationKey,
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
      const [result, payload] = args;
      const bookingCacheKey = bookingKeys.single(payload.id);
      const profileIds = map(result.entries || [], "profile_id");
      return Promise.all([
        cache.invalidateQueries({ queryKey: bookingCacheKey }),
        ...(payload.id ? [invalidateProfileWithBooking(payload.id)] : []),
        ...(!isEmpty(profileIds) ? profileIds.map((id) => invalidateProfile(id)) : [])
      ]);
    },
    ...options
  });

  const checkIsUpdating = useCallback(
    (bookingId: number) =>
      cache.isMutating<MutationFilters<any, any, { id: Id }>>({
        predicate: (mutation) => {
          const isUpdateMutation = isEmpty(xor(mutationKey, mutation.options.mutationKey));
          return isUpdateMutation && mutation.state.variables?.id === bookingId;
        }
      }),
    [cache]
  );

  return {
    mutation: updateMutation,
    update: updateMutation.mutateAsync,
    checkIsUpdating
  };
};
